import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

/**
 * Vanilla three.js particle field forming the word DELVENTURE.
 * Canvas element is React-owned (rendered as JSX) and passed to
 * WebGLRenderer via { canvas }. This avoids any manual DOM
 * appendChild / removeChild that would race with React's commit phase.
 */

const samplePositions = (text, width, height, density = 4) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const fontSize = Math.floor(height * 0.62);
  ctx.font = `900 ${fontSize}px "Clash Display", Inter, Arial, sans-serif`;
  ctx.fillText(text, width / 2, height / 2);
  const data = ctx.getImageData(0, 0, width, height).data;
  const points = [];
  for (let y = 0; y < height; y += density) {
    for (let x = 0; x < width; x += density) {
      const idx = (y * width + x) * 4;
      if (data[idx + 3] > 128) {
        const nx = (x - width / 2) / 8;
        const ny = -(y - height / 2) / 8;
        points.push([nx + (Math.random() - 0.5) * 0.15, ny + (Math.random() - 0.5) * 0.15, 0]);
      }
    }
  }
  return points;
};

const ParticleHero = ({ scrollProgressRef }) => {
  const mountRef = useRef(null);
  const canvasRef = useRef(null);
  const [reduced, setReduced] = useState(false);
  const pointer = useRef({ x: 0, y: 0 });
  const rafRef = useRef(0);

  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  useEffect(() => {
    if (reduced) return;
    const mount = mountRef.current;
    const canvasEl = canvasRef.current;
    if (!mount || !canvasEl) return;

    const isMobile = window.innerWidth < 768;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasEl,
      antialias: false,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.25 : 1.75));
    renderer.setSize(mount.clientWidth, mount.clientHeight, false);
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, mount.clientWidth / mount.clientHeight, 0.1, 200);
    camera.position.set(0, 0, 22);

    const w = isMobile ? 700 : 1400;
    const h = isMobile ? 180 : 240;
    const density = isMobile ? 6 : 4;
    let samples = samplePositions("DELVENTURE", w, h, density);
    const max = isMobile ? 4000 : 9000;
    if (samples.length > max) {
      const step = Math.ceil(samples.length / max);
      samples = samples.filter((_, i) => i % step === 0);
    }
    const n = samples.length;
    const target = new Float32Array(n * 3);
    const positions = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      target[i * 3] = samples[i][0];
      target[i * 3 + 1] = samples[i][1];
      target[i * 3 + 2] = samples[i][2];
      positions[i * 3] = (Math.random() - 0.5) * 60;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 30;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
    }
    const colors = new Float32Array(n * 3);
    const c1 = new THREE.Color("#6e5bff");
    const c2 = new THREE.Color("#00e0ff");
    for (let i = 0; i < n; i++) {
      const tx = target[i * 3];
      const t = (tx + 80) / 160;
      const c = c1.clone().lerp(c2, Math.max(0, Math.min(1, t)));
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: isMobile ? 0.14 : 0.11,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });

    const pts = new THREE.Points(geometry, material);
    pts.frustumCulled = false;
    scene.add(pts);

    const onPointer = (e) => {
      const r = mount.getBoundingClientRect();
      pointer.current.x = ((e.clientX - r.left) / r.width) * 2 - 1;
      pointer.current.y = -(((e.clientY - r.top) / r.height) * 2 - 1);
    };
    mount.addEventListener("mousemove", onPointer);

    const onResize = () => {
      const W = mount.clientWidth;
      const H = mount.clientHeight;
      renderer.setSize(W, H, false);
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    let visible = true;
    const io = new IntersectionObserver(
      (entries) => {
        visible = entries[0].isIntersecting;
      },
      { threshold: 0 }
    );
    io.observe(mount);

    const clock = new THREE.Clock();
    const lerp = 0.08;
    const repelR = 6;
    const repelR2 = repelR * repelR;

    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      if (!visible) return;
      const t = clock.getElapsedTime();
      const sp = (scrollProgressRef && scrollProgressRef.current) || 0;

      const viewHeight = 2 * Math.tan((camera.fov * Math.PI) / 360) * camera.position.z;
      const viewWidth = viewHeight * camera.aspect;
      const px = pointer.current.x * viewWidth * 0.5;
      const py = pointer.current.y * viewHeight * 0.5;

      const arr = geometry.attributes.position.array;
      for (let i = 0; i < n; i++) {
        const ix = i * 3;
        const tx = target[ix];
        const ty = target[ix + 1];
        const tz = target[ix + 2];

        const dissolve = sp;
        const driftY = -dissolve * 25 - dissolve * (Math.sin(t * 0.6 + i * 0.01) * 2);
        const driftX = dissolve * Math.sin(t * 0.4 + i * 0.02) * 8;
        const noiseAmt = dissolve * 6;

        let dxT = tx + driftX + Math.sin(t * 0.5 + i) * noiseAmt * 0.2;
        let dyT = ty + driftY;
        let dzT = tz + Math.cos(t * 0.3 + i) * noiseAmt * 0.2;

        const dxP = arr[ix] - px;
        const dyP = arr[ix + 1] - py;
        const d2 = dxP * dxP + dyP * dyP;
        if (d2 < repelR2) {
          const f = (1 - d2 / repelR2) * 1.4;
          dxT += dxP * f * 0.18;
          dyT += dyP * f * 0.18;
        }

        arr[ix] += (dxT - arr[ix]) * lerp;
        arr[ix + 1] += (dyT - arr[ix + 1]) * lerp;
        arr[ix + 2] += (dzT - arr[ix + 2]) * lerp;
      }
      geometry.attributes.position.needsUpdate = true;
      camera.position.x = Math.sin(t * 0.15) * 0.4;
      camera.position.y = Math.cos(t * 0.12) * 0.2;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      mount.removeEventListener("mousemove", onPointer);
      io.disconnect();
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      // No manual DOM removal — canvas is React-owned.
      // No forceContextLoss either — it breaks React StrictMode's
      // dev-mode double-invocation when remounting with the same canvas.
    };
  }, [reduced, scrollProgressRef]);

  if (reduced) {
    return (
      <div
        data-testid="hero-fallback"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 40% at 50% 50%, rgba(110,91,255,0.22), transparent 70%), radial-gradient(40% 30% at 50% 50%, rgba(0,224,255,0.18), transparent 70%), #f2f2f0",
        }}
      />
    );
  }

  return (
    <div ref={mountRef} className="absolute inset-0" data-testid="particle-hero">
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
};

export default ParticleHero;
